<template>
  <div class="facturacion-masiva">

    <!-- El progreso va arriba de todo: es lo primero que el usuario quiere ver
         cuando vuelve a la pantalla con un lote en curso. -->
    <facturacion-masiva-progreso
      ref="progreso"
      @finalizado="onFinalizado"
      @error="mostrarError"
    />

    <!-- modo page: filtros en sidebar, como el resto de los listados.
         modo dialog: filtros colapsados, porque el diálogo no da ancho. -->
    <v-row v-if="modo === 'page'" no-gutters>
      <v-col cols="12" md="3" lg="2">
        <facturacion-masiva-filters @updateFilters="onFiltros" />
      </v-col>
      <v-col cols="12" md="9" lg="10" class="pl-md-3 mt-3 mt-md-0">
        <facturacion-masiva-cola
          ref="cola"
          :filtros="filtros"
          :initial-orden-venta-ids="initialOrdenVentaIds"
          @emitir="emitir"
          @error="mostrarError"
        />
      </v-col>
    </v-row>

    <template v-else>
      <v-expansion-panels flat class="mb-3 filtros-dialogo">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div class="d-flex align-center">
              <v-icon small color="primary" class="mr-2">mdi-filter-variant</v-icon>
              <span>Filtros</span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <facturacion-masiva-filters compacto @updateFilters="onFiltros" />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <facturacion-masiva-cola
        ref="cola"
        :filtros="filtros"
        :initial-orden-venta-ids="initialOrdenVentaIds"
        @emitir="emitir"
        @error="mostrarError"
      />
    </template>

    <v-dialog v-model="confirmar" max-width="480">
      <v-card>
        <v-card-title class="text-h6">Confirmar facturación</v-card-title>
        <v-card-text>
          Se van a emitir <strong>{{ totalAConfirmar }}</strong>
          facturas. El proceso corre en segundo plano: podés cerrar esta pantalla
          y seguir trabajando.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmar = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="emitiendo" @click="confirmarEmision">
            Facturar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="6000">
      {{ mensaje }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import FacturacionProvider from "../../providers/FacturacionProvider";
import FacturacionMasivaFilters from "./FacturacionMasivaFilters";
import FacturacionMasivaCola from "./FacturacionMasivaCola";
import FacturacionMasivaProgreso from "./FacturacionMasivaProgreso";

export default {
  name: "FacturacionMasiva",

  components: {
    FacturacionMasivaFilters,
    FacturacionMasivaCola,
    FacturacionMasivaProgreso,
  },

  props: {
    /** 'page' dentro del módulo de Facturación, 'dialog' desde Órdenes de Venta. */
    modo: {
      type: String,
      default: "page",
      validator: (v) => ["page", "dialog"].includes(v),
    },
    initialOrdenVentaIds: { type: Array, default: null },
  },

  data() {
    return {
      filtros: {},
      confirmar: false,
      emitiendo: false,
      pendienteDeConfirmar: null,
      snackbar: false,
      snackbarColor: "success",
      mensaje: "",
    };
  },

  computed: {
    totalAConfirmar() {
      return this.pendienteDeConfirmar ? this.pendienteDeConfirmar.totalEstimado : 0;
    },
  },

  methods: {
    onFiltros(filtros) {
      this.filtros = filtros;
    },

    emitir(payload) {
      this.pendienteDeConfirmar = payload;
      this.confirmar = true;
    },

    async confirmarEmision() {
      this.emitiendo = true;
      const payload = this.pendienteDeConfirmar;
      try {
        const r = await FacturacionProvider.facturacionMasivaEmitir({
          filtros: this.filtrosParaEmitir(),
          modoSeleccion: payload.modoSeleccion,
          ordenVentaIds: payload.ordenVentaIds,
          excluirOrdenVentaIds: payload.excluirOrdenVentaIds,
          enviarEmail: payload.enviarEmail,
        });

        const lote = r.data.facturacionMasivaEmitir;
        this.confirmar = false;

        // El lote recién arranca: todavía no emitió nada. El resumen real llega
        // por el evento 'finalizado'.
        this.mostrarOk(
          `Facturación iniciada: ${lote.totalPendientes} órdenes en proceso.`
        );
        this.$emit("emitido", lote);

        const completo = await FacturacionProvider.facturacionMasivaLote(lote.id);
        this.$refs.progreso.seguir(completo.data.facturacionMasivaLote);
      } catch (e) {
        this.mostrarError(this.limpiarError(e.message));
      } finally {
        this.emitiendo = false;
      }
    },

    filtrosParaEmitir() {
      const base = { ...this.filtros };
      delete base.enviarEmail;
      if (this.initialOrdenVentaIds && this.initialOrdenVentaIds.length > 0) {
        base.ordenVentaIds = this.initialOrdenVentaIds;
      }
      return base;
    },

    onFinalizado(lote) {
      if (lote.estado === "PROCESANDO") return;

      const resumen = `${lote.totalEmitidas} emitidas`
        + (lote.totalErrores ? `, ${lote.totalErrores} con error` : "")
        + (lote.totalOmitidas ? `, ${lote.totalOmitidas} omitidas` : "");

      if (lote.totalErrores > 0) this.mostrarAdvertencia(`Facturación terminada: ${resumen}`);
      else this.mostrarOk(`Facturación terminada: ${resumen}`);

      // La cola cambió: lo emitido ya no está pendiente.
      if (this.$refs.cola) this.$refs.cola.recargar();
      this.$emit("finalizado", lote);
    },

    /** Apollo prefija los errores de servidor; al usuario no le sirve verlo. */
    limpiarError(mensaje) {
      return String(mensaje || "").replace(/^GraphQL error:\s*/, "");
    },

    mostrarOk(m) {
      this.mensaje = m;
      this.snackbarColor = "success";
      this.snackbar = true;
    },

    mostrarAdvertencia(m) {
      this.mensaje = m;
      this.snackbarColor = "warning";
      this.snackbar = true;
    },

    mostrarError(m) {
      this.mensaje = this.limpiarError(m);
      this.snackbarColor = "error";
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.filtros-dialogo {
  border: 1px solid #e1e8ed;
  border-radius: 8px;
}
</style>
